import {DeliveryInfoState} from '@web/slices/deliveryInfo/slice'
import {orderSheetProductInfoSelector, OrderSheetProductsState} from '@web/slices/orderSheetProducts/slice'
import {OrderTotalState} from '@web/slices/orderTotal/slice'
import {PointPaymentState} from '@web/slices/pointPayment/slice'

import {ArrivalGuaranteeByProductId} from './interfaces'

/** 도착보장날짜 변경 여부, 배송비 변경 여부 체크. */
export function getArrivalGuaranteeChangedInfo(
    state: {
        orderTotal: OrderTotalState
        deliveryInfo: DeliveryInfoState
        orderSheetProducts: OrderSheetProductsState
    },
    {
        updatedArrivalGuaranteesByOrderSheetProductId,
        deliveryFeeAmount: originDeliveryFeeAmount,
    }: {
        updatedArrivalGuaranteesByOrderSheetProductId: ArrivalGuaranteeByProductId
        deliveryFeeAmount?: number
    },
): Maybe<{
    isChanged: boolean
    arrivalDateChangedProductIds: string[]
}> {
    const {hasArrivalGuaranteeProduct} = orderSheetProductInfoSelector(state)
    if (!hasArrivalGuaranteeProduct) {
        return null
    }
    const {products} = orderSheetProductInfoSelector(state)
    const notifications = products.reduce<
        {
            oid: string
            mid: string
            name: string
            updatedTime: string
        }[]
    >((acc, product) => {
        const {
            id,
            name,
            arrivalGuaranteeDateTime,
            merchantInfo: {merchantProductId},
            deliveryInfo: {deliveryTagType},
        } = product

        const updatedArrivalGuaranteeDateTime =
            updatedArrivalGuaranteesByOrderSheetProductId[id]?.arrivalGuaranteeDateTime ?? ''

        const updatedDeliveryTagType = updatedArrivalGuaranteesByOrderSheetProductId[id]?.deliveryTagType

        const isArrivalGuaranteeDateTimeChanged =
            updatedArrivalGuaranteeDateTime && updatedArrivalGuaranteeDateTime !== arrivalGuaranteeDateTime

        const isDeliveryTagChanged = updatedDeliveryTagType && updatedDeliveryTagType !== deliveryTagType

        if (
            (isArrivalGuaranteeDateTimeChanged || isDeliveryTagChanged) &&
            acc.every(
                ({mid, updatedTime}) => !(mid === merchantProductId && updatedTime === updatedArrivalGuaranteeDateTime),
            )
        ) {
            return [
                ...acc,
                {
                    oid: id,
                    mid: merchantProductId,
                    name,
                    updatedTime: updatedArrivalGuaranteeDateTime,
                },
            ]
        }
        return acc
    }, [])

    const isRMIDChanged = products.some((product) => {
        const {rmid, id} = product
        return (
            updatedArrivalGuaranteesByOrderSheetProductId[id] &&
            updatedArrivalGuaranteesByOrderSheetProductId[id].rmid !== rmid
        )
    }, [])

    const isDeliveryTagChanged = products.some((product) => {
        const {
            deliveryInfo: {deliveryTagType},
            id,
        } = product

        return (
            updatedArrivalGuaranteesByOrderSheetProductId[id] &&
            updatedArrivalGuaranteesByOrderSheetProductId[id].deliveryTagType !== deliveryTagType
        )
    })

    const isDateTimeChanged = notifications.length > 0
    const isChanged = isRMIDChanged || isDateTimeChanged || isDeliveryTagChanged
    const deliveryFeeAmount = originDeliveryFeeAmount ?? state.orderTotal.deliveryFeeAmount
    if (state.orderTotal.deliveryFeeAmount === deliveryFeeAmount && isDateTimeChanged) {
        return {
            isChanged,
            arrivalDateChangedProductIds: notifications.map(({oid}) => oid),
        }
    }
    return {
        isChanged,
        arrivalDateChangedProductIds: [],
    }
}

/** 배송비 재조회시 지역별 추가배송비 초기화 메시지 타입 */
type MessageType = 'SIMPLE' | 'DETAIL'

/** 배송비 변경 시나리오 */
type ChangeScenario = 'arrivalGuaranteeChange' | 'usePointAll' | 'normal'

const ChangeDeliveryAreaExtraFeeMessageMap: Record<MessageType, Record<ChangeScenario, string>> = {
    SIMPLE: {
        arrivalGuaranteeChange:
            '지역별 추가 배송비 금액 및 N배송 상품의 도착일이 변경되었습니다. 입력하신 배송비 할인쿠폰 및 포인트 사용액이 모두 초기화됩니다. 다시 확인해주세요.',
        usePointAll:
            '지역별 추가 배송비 금액이 변경되어 입력하신 배송비 할인 쿠폰과 [항상 포인트 사용] 설정에 따라 포인트 사용액이 모두 초기화 됩니다.',
        normal: '지역별 추가 배송비 금액이 변경되어 입력하신 배송비 할인쿠폰 및 포인트 사용액이 모두 초기화 됩니다.',
    },
    DETAIL: {
        arrivalGuaranteeChange:
            '지역별 추가 배송비 금액 및 N배송 상품의 도착일이 변경되었습니다. 입력하신 할인/쿠폰, 카드사 결제 할인, 포인트 사용액을 모두 초기화합니다. 다시 확인해주세요.',
        usePointAll:
            '지역별 추가 배송비 금액이 변경되어 입력하신 할인/쿠폰, 카드사 결제 할인, 항상 포인트 사용 설정에 따라 포인트 사용액이 모두 초기화됩니다.',
        normal: '지역별 추가 배송비 금액이 변경되어 입력하신 할인/쿠폰, 카드사 결제 할인, 포인트 사용액을 모두 초기화합니다.',
    },
}

/**
 * 배송비 변경 시나리오를 결정합니다.
 * @param shouldArrivalGuaranteeChange - 도착보장 변경 여부
 * @param isUsePointAll - 항상 포인트 전액 사용 여부
 * @returns 변경 시나리오 타입
 */
function getChangeScenario(shouldArrivalGuaranteeChange: boolean, isUsePointAll: boolean): ChangeScenario {
    if (shouldArrivalGuaranteeChange) {
        return 'arrivalGuaranteeChange'
    }
    if (isUsePointAll) {
        return 'usePointAll'
    }
    return 'normal'
}

/**
 * 배송비 재조회 시 지역별 추가 배송비 변경 안내 메시지를 반환합니다.
 *
 * @param state - 주문 총액과 포인트 결제 정보를 포함하는 상태
 * @param params - 변경된 배송비, 도착보장 변경 여부, 상세 메시지 표시 여부
 * @returns 배송비 변경 안내 메시지. 변경되지 않았으면 빈 문자열 반환
 */
export function getChangeDeliveryAreaExtraFeeMessage(
    state: {
        orderTotal: OrderTotalState
        pointPayment: PointPaymentState
    },
    {
        deliveryFeeAmount,
        shouldArrivalGuaranteeChange,
        shouldMoreDetailMessage,
    }: {
        deliveryFeeAmount: number
        shouldArrivalGuaranteeChange: boolean
        shouldMoreDetailMessage: boolean
    },
): string {
    const isDeliveryFeeChanged = state.orderTotal.deliveryFeeAmount !== deliveryFeeAmount

    if (!isDeliveryFeeChanged) {
        return ''
    }

    const messageType: MessageType = shouldMoreDetailMessage ? 'DETAIL' : 'SIMPLE'
    const scenario = getChangeScenario(shouldArrivalGuaranteeChange, state.pointPayment.naverPayPoint.usePointAll)

    return ChangeDeliveryAreaExtraFeeMessageMap[messageType][scenario]
}
